import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { listAllVouchers } from '../../vouchers/services/voucherService';
import { formatDate, formatDateTime } from '../../vouchers/utils/voucherDates';
import { createVoucherExportWorker } from '../utils/createVoucherExportWorker';
import { ROUTES } from '../../../shared/config/routes';
import PaginationControls from '../../../shared/components/ui/PaginationControls';

const PAGE_SIZE = 5;
const EXPORT_BATCH_SIZE = 250;

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function AdminVoucherListPage() {
  const [vouchers, setVouchers] = useState([]);
  const [meta, setMeta] = useState({ page: 0, totalPages: 1, totalElements: 0 });
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ name: '', phone: '', createdToday: false, activeOnly: false });
  const [appliedFilters, setAppliedFilters] = useState({ name: '', phone: '', createdToday: false, activeOnly: false });
  const [exportJob, setExportJob] = useState({
    status: 'idle',
    currentPage: 0,
    totalPages: 0,
    totalRows: 0,
    message: '',
  });
  const exportWorkerRef = useRef(null);
  const hasLoadedTableRef = useRef(false);

  const fetchPage = useCallback(async (page = 0, nextFilters = {}) => {
    setLoading(!hasLoadedTableRef.current);
    setPageLoading(hasLoadedTableRef.current);
    setError('');

    try {
      const result = await listAllVouchers({
        page,
        size: PAGE_SIZE,
        sortBy: 'expiresAt',
        direction: 'desc',
        name: nextFilters.name || undefined,
        phone: nextFilters.phone || undefined,
        createdToday: nextFilters.createdToday || undefined,
        activeOnly: nextFilters.activeOnly || undefined,
      });
      setVouchers(result.items);
      setMeta({
        page: result.page,
        totalPages: result.totalPages,
        totalElements: result.totalElements,
      });
      hasLoadedTableRef.current = true;
    } catch (_error) {
      setError('No se pudieron cargar los bonos.');
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(0, { name: '', phone: '', createdToday: false, activeOnly: false });
  }, [fetchPage]);

  useEffect(() => {
    const worker = createVoucherExportWorker();
    exportWorkerRef.current = worker;

    worker.onmessage = (event) => {
      if (event.data?.type !== 'done') {
        return;
      }

      downloadBlob(event.data.blob, event.data.filename);
      setExportJob({
        status: 'done',
        currentPage: 0,
        totalPages: 0,
        totalRows: 0,
        message: 'Excel generado en segundo plano y descargado.',
      });

      window.setTimeout(() => {
        setExportJob((previous) => (
          previous.status === 'done'
            ? { status: 'idle', currentPage: 0, totalPages: 0, totalRows: 0, message: '' }
            : previous
        ));
      }, 4000);
    };

    return () => {
      worker.terminate();
    };
  }, []);

  const hasAppliedFilters = Boolean(appliedFilters.name || appliedFilters.phone || appliedFilters.createdToday || appliedFilters.activeOnly);
  const hasPendingFilterChanges = (
    filters.name !== appliedFilters.name
    || filters.phone !== appliedFilters.phone
    || filters.createdToday !== appliedFilters.createdToday
    || filters.activeOnly !== appliedFilters.activeOnly
  );

  function handleChange(event) {
    const { name, type, value, checked } = event.target;
    setFilters((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFilters(filters);
    fetchPage(0, filters);
  }

  function handleClearFilters() {
    const cleared = { name: '', phone: '', createdToday: false, activeOnly: false };
    setFilters(cleared);
    setAppliedFilters(cleared);
    fetchPage(0, cleared);
  }

  async function handleExportAll() {
    if (!exportWorkerRef.current || exportJob.status === 'fetching' || exportJob.status === 'building') {
      return;
    }

    setExportJob({
      status: 'fetching',
      currentPage: 0,
      totalPages: 0,
      totalRows: 0,
      message: 'Preparando exportacion en segundo plano...',
    });

    try {
      const rows = [];
      let currentPage = 0;
      let totalPages = 1;
      let totalRows = 0;

      while (currentPage < totalPages) {
        const result = await listAllVouchers({
          page: currentPage,
          size: EXPORT_BATCH_SIZE,
          sortBy: 'expiresAt',
          direction: 'desc',
          name: appliedFilters.name || undefined,
          phone: appliedFilters.phone || undefined,
          createdToday: appliedFilters.createdToday || undefined,
          activeOnly: appliedFilters.activeOnly || undefined,
        });

        rows.push(...result.items.map((voucher) => ({
          voucherCode: voucher.voucherCode,
          customerName: voucher.customerName,
          phone: voucher.phone || '',
          email: voucher.email || '',
          hoursPurchased: voucher.hoursPurchased,
          hoursUsed: voucher.hoursUsed,
          hoursAvailable: voucher.hoursAvailable,
          purchaseDate: formatDateTime(voucher.purchaseDate),
          expiresAt: formatDate(voucher.expiresAt),
          statusLabel: voucher.hoursAvailable > 0 ? 'Activo' : 'Agotado',
        })));

        totalPages = result.totalPages;
        totalRows = result.totalElements;

        setExportJob({
          status: 'fetching',
          currentPage: currentPage + 1,
          totalPages,
          totalRows,
          message: `Recopilando bonos ${Math.min(currentPage + 1, totalPages)} / ${totalPages}`,
        });

        currentPage++;
        await new Promise((resolve) => window.setTimeout(resolve, 0));
      }

      setExportJob({
        status: 'building',
        currentPage: totalPages,
        totalPages,
        totalRows,
        message: 'Construyendo Excel en segundo plano...',
      });

      exportWorkerRef.current.postMessage({
        type: 'build-csv',
        rows,
        filename: `bonos_mundolandia_${new Date().toISOString().slice(0, 10)}.csv`,
      });
    } catch (_error) {
      setError('Error al exportar los bonos.');
      setExportJob({
        status: 'error',
        currentPage: 0,
        totalPages: 0,
        totalRows: 0,
        message: 'No se pudo completar la exportacion.',
      });
    } finally {
      if (exportJob.status === 'fetching') {
        // noop - state handled above
      }
    }
  }

  const isExporting = exportJob.status === 'fetching' || exportJob.status === 'building';

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] shadow-[0_24px_60px_rgba(15,23,42,0.06)] p-8 backdrop-blur-xl"
      >
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.32em] text-slate-400">Bonos</p>
            <h1 className="mt-3 text-[54px] leading-[0.96] font-semibold tracking-[-0.06em] text-slate-950">
              Gestión de bonos
            </h1>
            <p className="mt-4 text-lg text-slate-500">
              Todos los bonos del sistema con paginación de servidor. Filtra por nombre o teléfono y exporta el Excel en segundo plano sin bloquear la pantalla.
            </p>
          </div>
          <div className="flex flex-col items-start gap-3">
            <button
              type="button"
              onClick={handleExportAll}
              disabled={isExporting || meta.totalElements === 0}
              className="self-start rounded-[22px] bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-4 font-medium shadow-[0_14px_24px_rgba(16,185,129,0.22)] transition disabled:opacity-50 flex items-center gap-2"
            >
              {isExporting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Exportando en segundo plano...
                </>
              ) : (
                <>📥 Descargar Excel</>
              )}
            </button>

            {exportJob.status !== 'idle' && (
              <div className={`rounded-2xl px-4 py-3 text-sm ${
                exportJob.status === 'error'
                  ? 'border border-red-200 bg-red-50 text-red-600'
                  : exportJob.status === 'done'
                    ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border border-emerald-200 bg-emerald-50/80 text-emerald-700'
              }`}>
                <p className="font-medium">{exportJob.message}</p>
                {exportJob.status === 'fetching' && exportJob.totalPages > 0 && (
                  <p className="mt-1 text-xs opacity-80">
                    {exportJob.totalRows} registros estimados · lote {exportJob.currentPage} de {exportJob.totalPages}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <form onSubmit={handleFilterSubmit} className="mt-8 grid gap-4 lg:grid-cols-[1fr_1fr_auto_auto]">
          <input
            type="text"
            name="name"
            value={filters.name}
            onChange={handleChange}
            placeholder="Filtrar por nombre…"
            className="app-input"
          />
          <input
            type="tel"
            name="phone"
            value={filters.phone}
            onChange={handleChange}
            placeholder="Filtrar por teléfono…"
            className="app-input"
          />
          <label className="flex items-center gap-3 rounded-[22px] border border-[var(--app-border)] bg-[rgba(245,245,247,0.72)] px-5 py-4 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              name="createdToday"
              checked={filters.createdToday}
              onChange={handleChange}
              className="h-4 w-4 accent-[var(--app-accent)]"
            />
            Bonos creados hoy
          </label>
          <label className="flex items-center gap-3 rounded-[22px] border border-[var(--app-border)] bg-[rgba(245,245,247,0.72)] px-5 py-4 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              name="activeOnly"
              checked={filters.activeOnly}
              onChange={handleChange}
              className="h-4 w-4 accent-[var(--app-accent)]"
            />
            Solo activos
          </label>
          <button
            type="submit"
            disabled={pageLoading}
            className="rounded-[22px] bg-[#0a84ff] text-white px-6 py-4 hover:bg-[#0077ed] transition font-medium shadow-[0_14px_24px_rgba(10,132,255,0.22)] disabled:opacity-60"
          >
            Buscar
          </button>
          <div className="flex gap-4 lg:col-span-2">
            <button
              type="button"
              onClick={handleClearFilters}
              disabled={pageLoading}
              className="rounded-[22px] border border-[var(--app-border)] bg-[rgba(245,245,247,0.9)] px-6 py-4 font-medium text-slate-600 hover:bg-white transition disabled:opacity-60"
            >
              Limpiar
            </button>
          </div>
        </form>
      </motion.div>

      {error && <p className="text-red-600 font-semibold px-2">{error}</p>}

      {/* Results count */}
      <div className="px-2 flex items-center justify-between">
        <span className="text-sm text-slate-500">
          {loading
            ? 'Cargando...'
            : `${meta.totalElements} bono${meta.totalElements !== 1 ? 's' : ''} en total · Página ${meta.page + 1} de ${meta.totalPages}`}
        </span>
        {hasPendingFilterChanges ? (
          <span className="text-sm text-amber-500">
            Pulsa Buscar para aplicar los cambios de filtro
          </span>
        ) : hasAppliedFilters ? (
          <span className="text-sm text-slate-400">
            Filtros aplicados sobre la paginación del servidor
          </span>
        ) : null}
      </div>

      {/* Table */}
      <div className="relative rounded-[28px] border border-[var(--app-border)] bg-[var(--app-surface)] shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl overflow-hidden">
        {pageLoading && !loading && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/45 backdrop-blur-[2px]">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/90 px-4 py-2 text-sm font-medium text-slate-700 shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
              <span className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-[var(--app-accent)] animate-spin" />
              Cargando página...
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed text-sm text-left border-collapse">
            <thead className="bg-[rgba(245,245,247,0.9)] text-slate-600">
              <tr>
                <th className="w-[160px] p-4 border-b border-[var(--app-border)] font-medium">Código</th>
                <th className="w-[130px] p-4 border-b border-[var(--app-border)] font-medium">Titular</th>
                <th className="w-[150px] p-4 border-b border-[var(--app-border)] font-medium">Teléfono</th>
                <th className="w-[280px] p-4 border-b border-[var(--app-border)] font-medium">Email</th>
                <th className="w-[110px] p-4 border-b border-[var(--app-border)] font-medium">Compradas</th>
                <th className="w-[130px] p-4 border-b border-[var(--app-border)] font-medium">Disponibles</th>
                <th className="w-[130px] p-4 border-b border-[var(--app-border)] font-medium">Compra</th>
                <th className="w-[150px] p-4 border-b border-[var(--app-border)] font-medium">Vencimiento</th>
                <th className="w-[140px] p-4 border-b border-[var(--app-border)] font-medium">Estado</th>
                <th className="w-[180px] p-4 border-b border-[var(--app-border)] font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading && vouchers.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-10 text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-slate-300 border-t-[var(--app-accent)] rounded-full animate-spin" />
                      Cargando bonos...
                    </div>
                  </td>
                </tr>
              ) : vouchers.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-12 text-slate-500">
                    {hasAppliedFilters ? 'Sin resultados para los filtros aplicados' : 'No hay bonos registrados'}
                  </td>
                </tr>
              ) : (
                vouchers.map((voucher) => {
                  const isActive = voucher.hoursAvailable > 0;
                  return (
                    <tr key={voucher.id} className="hover:bg-[rgba(245,245,247,0.7)] transition">
                      <td className="p-4 border-b border-[var(--app-border)] font-medium text-[var(--app-accent)] break-words">
                        {voucher.voucherCode}
                      </td>
                      <td className="p-4 border-b border-[var(--app-border)] font-medium text-slate-900 break-words">
                        {voucher.customerName}
                      </td>
                      <td className="p-4 border-b border-[var(--app-border)] text-slate-600">
                        {voucher.phone || '—'}
                      </td>
                      <td className="p-4 border-b border-[var(--app-border)] text-slate-500 break-all">
                        {voucher.email || '—'}
                      </td>
                      <td className="p-4 border-b border-[var(--app-border)] text-slate-700">
                        {voucher.hoursPurchased}h
                      </td>
                      <td className="p-4 border-b border-[var(--app-border)]">
                        <span className={`font-semibold ${isActive ? 'text-emerald-600' : 'text-red-500'}`}>
                          {voucher.hoursAvailable}h
                        </span>
                      </td>
                      <td className="p-4 border-b border-[var(--app-border)] text-slate-500">
                        {formatDateTime(voucher.purchaseDate)}
                      </td>
                      <td className="p-4 border-b border-[var(--app-border)] text-slate-500">
                        {formatDate(voucher.expiresAt)}
                      </td>
                      <td className="p-4 border-b border-[var(--app-border)]">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                          isActive
                            ? 'bg-emerald-50 border border-emerald-200 text-emerald-600'
                            : 'bg-red-50 border border-red-200 text-red-500'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-red-400'}`} />
                          {isActive ? 'Activo' : 'Agotado'}
                        </span>
                      </td>
                      <td className="p-4 border-b border-[var(--app-border)] w-[180px]">
                        <Link
                          to={ROUTES.adminVoucherDetail.replace(':voucherId', encodeURIComponent(String(voucher.id ?? '')))}
                          state={{ voucherPreview: voucher }}
                          className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-2xl bg-[rgba(245,245,247,0.9)] border border-[var(--app-border)] px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-white transition"
                        >
                          Ver detalle
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <PaginationControls
          currentPage={meta.page}
          totalPages={meta.totalPages}
          onPageChange={(nextPage) => fetchPage(nextPage, appliedFilters)}
          disabled={loading || pageLoading}
        />
      </div>
    </div>
  );
}
